import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { useReveal } from "@/hooks/useReveal";

const services = [
  {
    title: "Custom Software Development",
    tagline: "Built for your exact needs",
    description:
      "Web applications, mobile apps, enterprise software, and cloud solutions crafted from scratch to match your business processes. We deliver scalable, secure, and maintainable code that grows with your company.",
    icon: "ri-code-s-slash-line",
    color: "#D4FF3F",
    features: [
      "Full-stack development",
      "API integrations",
      "Cloud deployment",
      "Ongoing maintenance",
      "Mobile applications",
      "Legacy modernization",
    ],
  },
  {
    title: "IT Consulting",
    tagline: "Smart technology decisions",
    description:
      "Strategic guidance to optimize your tech stack, modernize legacy systems, and implement tools that drive efficiency. We help you choose the right architecture, frameworks, and cloud platforms for long-term success.",
    icon: "ri-lightbulb-flash-line",
    color: "#F7C59F",
    features: [
      "Tech stack audit",
      "Process optimization",
      "Digital transformation",
      "Security review",
      "Cloud migration strategy",
      "Architecture design",
    ],
  },
  {
    title: "Digital Marketing",
    tagline: "Grow your audience",
    description:
      "Comprehensive marketing strategies including SEO, paid advertising, content marketing, and brand positioning. We turn traffic into leads and leads into loyal customers through data-driven campaigns.",
    icon: "ri-megaphone-line",
    color: "#E2724B",
    features: [
      "SEO & content strategy",
      "Paid advertising",
      "Social media management",
      "Analytics & reporting",
      "Brand positioning",
      "Conversion optimization",
    ],
  },
  {
    title: "Full-Cycle Delivery",
    tagline: "From idea to launch",
    description:
      "End-to-end project delivery: business analysis, UI/UX design, development, testing, launch, and post-launch support. One team, one process, one accountable partner from concept to growth.",
    icon: "ri-rocket-line",
    color: "#D4FF3F",
    features: [
      "Business analysis",
      "UI/UX design",
      "QA & testing",
      "Post-launch support",
      "DevOps & CI/CD",
      "Performance monitoring",
    ],
  },
];

const processSteps = [
  {
    step: "01",
    title: "Discovery & Analysis",
    body: "We dive deep into your business goals, user needs, and technical requirements. Every project starts with thorough research and strategic planning.",
  },
  {
    step: "02",
    title: "Design & Architecture",
    body: "Our UI/UX designers and solution architects craft intuitive interfaces and robust system architecture tailored to your specific use case.",
  },
  {
    step: "03",
    title: "Development & Testing",
    body: "Experienced developers bring designs to life with clean, scalable code. Rigorous QA ensures everything works flawlessly before launch.",
  },
  {
    step: "04",
    title: "Launch & Growth",
    body: "We deploy your solution and provide ongoing support. Our marketing team helps you reach your audience and scale your product effectively.",
  },
];

export default function ServicesPage() {
  const heroRef = useReveal<HTMLDivElement>();
  const listRef = useReveal<HTMLDivElement>();
  const processRef = useReveal<HTMLDivElement>();

  return (
    <SiteLayout navbarVariant="overlay">
      {/* Hero */}
      <section className="relative h-[420px] md:h-[560px] overflow-hidden bg-ink">
        <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/40 to-ink" />
        <div
          ref={heroRef}
          className="relative h-full flex flex-col justify-end mx-auto max-w-[1320px] px-6 md:px-10 pb-16"
        >
          <div data-reveal>
            <span className="text-xs uppercase tracking-widest2 text-cream/60 mb-4 block">
              What we offer
            </span>
            <h1 className="text-display text-5xl md:text-7xl text-cream leading-[0.95]">
              Our <span className="italic font-light">Services</span>.
            </h1>
            <p className="mt-6 max-w-xl text-cream/70 text-base md:text-lg leading-relaxed">
              End-to-end digital solutions tailored for your business. From custom software
              to marketing — we build, launch, and grow products that matter.
            </p>
          </div>
        </div>
      </section>

      {/* Services list */}
      <section ref={listRef} className="bg-cream py-20 md:py-28">
        <div className="mx-auto max-w-[1320px] px-6 md:px-10">
          <div className="space-y-5">
            {services.map((s, i) => (
              <div
                key={s.title}
                data-reveal
                data-reveal-delay={`${i * 60}`}
                className="group grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 rounded-3xl bg-sand/40 border border-ink/5 hover:border-ink/15 transition-all p-8 md:p-10"
              >
                <div className="md:col-span-1">
                  <div
                    className="w-12 h-12 flex items-center justify-center rounded-2xl"
                    style={{ backgroundColor: s.color }}
                  >
                    <i className={`${s.icon} text-xl text-ink`} />
                  </div>
                </div>
                <div className="md:col-span-7">
                  <h3 className="text-display text-2xl md:text-3xl mb-1">
                    {s.title}
                  </h3>
                  <p className="text-sm text-ink/60 italic mb-4">{s.tagline}</p>
                  <p className="text-ink/70 text-sm md:text-base leading-relaxed">
                    {s.description}
                  </p>
                </div>
                <div className="md:col-span-4">
                  <div className="flex flex-wrap gap-2">
                    {s.features.map((f) => (
                      <span
                        key={f}
                        className="rounded-full bg-ink/5 text-ink/70 px-3 py-1.5 text-[11px] uppercase tracking-widest2"
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How we work */}
      <section ref={processRef} className="bg-sand py-20 md:py-28 border-t border-ink/8">
        <div className="mx-auto max-w-[1320px] px-6 md:px-10">
          <div className="mb-10" data-reveal>
            <span className="text-xs uppercase tracking-widest2 text-ink/50">
              How we work
            </span>
            <h2 className="mt-4 text-display text-3xl md:text-5xl">
              From idea to <span className="italic font-light">impact</span>.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {processSteps.map((p, i) => (
              <div
                key={p.step}
                data-reveal
                data-reveal-delay={`${i * 80}`}
                className="rounded-3xl bg-cream border border-ink/5 p-8 hover:border-ink/15 transition-all"
              >
                <span className="text-display text-3xl text-ink/20 block mb-4">
                  {p.step}
                </span>
                <h3 className="text-display text-xl mb-2">{p.title}</h3>
                <p className="text-ink/60 text-sm leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-cream pb-20 md:pb-28">
        <div className="mx-auto max-w-[1320px] px-6 md:px-10">
          <div
            className="relative rounded-[32px] overflow-hidden min-h-[420px] md:min-h-[520px] flex items-end"
            data-reveal
          >
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20modern%20technology%20background%20with%20soft%20flowing%20lines%20geometric%20shapes%20digital%20network%20connections%20warm%20cream%20charcoal%20and%20muted%20green%20tones%20professional%20corporate%20aesthetic%20minimal%20clean&width=1800&height=1100&seq=vantory-services-cta&orientation=landscape"
              alt="Digital solutions"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/30 to-ink/20" />

            <div className="absolute top-10 md:top-14 left-6 md:left-12 right-6 md:right-12">
              <span className="text-xs uppercase tracking-widest2 text-cream/80">
                Start your project
              </span>
              <h2 className="text-display text-cream text-4xl md:text-6xl leading-[0.95] mt-4 text-balance max-w-2xl">
                Let&apos;s discuss your
                <br />
                next <span className="italic font-light">big idea</span>.
              </h2>
            </div>

            <div className="relative z-10 p-6 md:p-12 w-full flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <Link
                to="/contact"
                className="group inline-flex items-center gap-3 rounded-full bg-cream text-ink pl-7 pr-2 py-2 text-sm font-semibold uppercase tracking-widest2 cursor-pointer whitespace-nowrap hover:bg-lime-accent transition-colors"
              >
                Get in touch
                <span className="w-11 h-11 flex items-center justify-center rounded-full bg-ink text-cream group-hover:rotate-[-45deg] transition-transform duration-300">
                  <i className="ri-arrow-right-line text-base" />
                </span>
              </Link>
              <div className="text-cream/85 text-sm md:text-base max-w-sm text-left md:text-right">
                Free consultation. No commitment. We&apos;ll analyze your needs and propose the right
                solution for your business.
              </div>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}